import React from 'react';
import { useLatestVersion } from '../data/changelogData';
import './StaticPages.css';

function About() {
  const version = useLatestVersion();

  return (
    <div className="static-page-wrapper animate-up">
      <div className="static-content-card">
        <div className="static-page-header">
          <h1 className="static-page-title">The DevTracker Vision</h1>
          <span className="static-page-version">Platform {version}</span>
          <p className="static-page-subtitle">A high-fidelity analytics ecosystem designed to unify the world's technical identities into a single, verifiable digital footprint.</p>
          <div className="static-page-divider"></div>
        </div>

        <div className="legal-content-body">
          <section className="about-hero-text">
            <p>
              In the modern engineering landscape, technical reputation is the most valuable currency. 
              However, this reputation is often fragmented across dozens of platforms—GitHub for 
              collaboration, LeetCode for algorithms, and Codeforces for competitive programming. 
              <strong>DevTracker</strong> was engineered to solve this fragmentation by providing a 
              unified, neural-authenticated oversight of an engineer's true trajectory.
            </p>
          </section>

          <div className="about-grid">
            <div className="feature-box">
              <div className="feature-icon-ring">
                <i className="fa-solid fa-layer-group"></i>
              </div>
              <h4>Metadata Aggregation</h4>
              <p>We ingest thousands of public data points from global technical nodes to create a real-time skill tree of your professional activity.</p>
            </div>
            <div className="feature-box">
              <div className="feature-icon-ring">
                <i className="fa-solid fa-brain"></i>
              </div>
              <h4>Neural DNA Scoring</h4>
              <p>The <strong>DAIV-2000</strong> logic engine analyzes commit depth, algorithmic complexity, and consistency to generate a verified DNA Score.</p>
            </div>
            <div className="feature-box">
              <div className="feature-icon-ring">
                <i className="fa-solid fa-shield-check"></i>
              </div>
              <h4>Certified Identity</h4>
              <p>Every profile is assigned a unique SUID and QR-authenticator, ensuring that your professional record is verifiable by any entity.</p>
            </div>
          </div>

          <section className="about-depth-section">
            <h2><i className="fa-solid fa-code-branch"></i> The Fragmentation Problem</h2>
            <p>
              Before DevTracker, a developer's success on one platform was invisible to another. 
              A 2200+ rating on Codeforces held no weight on a GitHub-centric resume. Our platform 
              bridges these gaps using the <strong>Origin Core</strong> architecture, which translates 
              disparate metrics into a singular, authoritative professional language.
            </p>
          </section>

          <section className="about-depth-section">
            <h2><i className="fa-solid fa-bolt"></i> Engineering Philosophy</h2>
            <p>
              We adhere to three core pillars of platform integrity:
            </p>
            <ul className="static-list">
              <li>
                <strong>Technical Sovereignty:</strong> We only access public data strands. 
                Your private credentials remain entirely under your control—we never ask for passwords.
              </li>
              <li>
                <strong>Real-Time Synchronization:</strong> The platform utilizes professional API 
                proxies to ensure that your latest achievements are visible globaly within milliseconds.
              </li>
              <li>
                <strong>High-Fidelity Representation:</strong> We believe that an engineer's 
                presentation should match the quality of their code. Our Traditional Professional 
                aesthetic is designed for authoritative clarity.
              </li>
            </ul>
          </section>

          <section className="about-depth-section">
            <h2><i className="fa-solid fa-microchip"></i> Infrastructure: DAIV-2000</h2>
            <p>
              Behind the glassmorphism lies the <strong>DAIV-2000 (Director of Artificial Intelligence & Verification)</strong>. 
              This engine performs multi-dimensional analysis on your technical footprint, 
              identifying "Developer Archetypes" and calculating entropy markers to ensure 
              the identity remains authentic and free from automated spoofing.
            </p>
          </section>

          <div className="legal-document-id">
            PLATFORM_MANIFESTO_AUTH: 0X-DT-VISION-7733-SYNC
          </div>
        </div>
      </div>
    </div>
  );
}

export default About;
